import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, startAuthentication, WebAuthnAbortService } from '@simplewebauthn/browser'
import { createFileRoute, redirect, useSearch } from '@tanstack/react-router'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'

import Errors from '@tandem/ui/Errors'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC, useTRPCClient } from '@/trpc'
import PasskeyCreate from '@/auth/PasskeyCreate'

import $layout from '@tandem/ui/layout.css'
import $type from '@tandem/ui/type.css'
import $anim from '@tandem/ui/anim.css'

import SplashLayout from '@/layouts/Splash'

import ArrowRightCircleFillIcon from '@tandem/ui/icons/arrow.right.circle.fill.svg?react'
import $ from './-styles.css'

export const Route = createFileRoute('/login/')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || undefined,
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated && !search.token) {
      throw redirect({ to: '/' })
    }
  },
  component: LoginPage,
})

let back = () => {
  let to: string | null = null
  try { to = localStorage.getItem('back'); localStorage.removeItem('back') } catch { /* home */ }
  return to?.startsWith('/') && !to.startsWith('//') ? to : '/'
}

function LoginPage() {
  let trpc = useTRPC()
  let client = useTRPCClient()
  let search = useSearch({ strict: false }) as { token?: string }
  let isDev = import.meta.env.DEV
  let [offerPasskey, setOfferPasskey] = useState(false)
  let [passkeyAutofill, setPasskeyAutofill] = useState(true)
  let [passkeyError, setPasskeyError] = useState('')
  let finish = () => window.location.assign(back())

  let sendInvitation = useMutation(trpc.auth.sendInvitation.mutationOptions())
  async function onEmailSignIn() {
    window.history.replaceState(null, '', '/login')
    if (browserSupportsWebAuthn()) {
      let keys = await client.auth.passkeys.list.query().catch(() => null)
      if (keys?.length === 0) {
        setOfferPasskey(true)
        return
      }
    }
    finish()
  }
  let devSignIn = useMutation(trpc.auth.devSignIn.mutationOptions({ onSuccess: onEmailSignIn }))
  let verifyMagicLink = useMutation(trpc.auth.signIn.mutationOptions({ onSuccess: onEmailSignIn }))

  let form = useForm({
    canSubmitWhenInvalid: true,
    defaultValues: {
      token: search.token || '',
      email: ''
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        setPasskeyAutofill(false)
        if (value.token) {
          await verifyMagicLink.mutateAsync({ token: value.token })
        } else if (value.email) {
          if (isDev) await devSignIn.mutateAsync({ email: value.email })
          else await sendInvitation.mutateAsync({ email: value.email, loginUrl: window.location.origin + '/login' })
        }
      }
    }
  })
  
  let token = useStore(form.store, (state) => state.values.token)
  useEffect(() => {
    if (token) {
      form.handleSubmit()
    }
  }, [token, form])

  let emailInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    let timer = setTimeout(() => emailInputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!passkeyAutofill || search.token || sendInvitation.isSuccess || !browserSupportsWebAuthn()) return
    let controller = new AbortController()
    let { signal } = controller
    async function signIn() {
      let retryDelay = 1000
      while (!signal.aborted) {
        let selected = false
        try {
          if (!await browserSupportsWebAuthnAutofill()) return
          if (signal.aborted) return
          let optionsJSON = await client.auth.passkeys.authenticationOptions.mutate(undefined, { signal })
          if (signal.aborted) return
          let response = await startAuthentication({ optionsJSON, useBrowserAutofill: true })
          if (signal.aborted) return
          selected = true
          await client.auth.passkeys.signIn.mutate(response, { signal })
          if (!signal.aborted) window.location.assign(back())
          return
        } catch (error) {
          if (signal.aborted) return
          let cancelled = error instanceof Error && ['NotAllowedError', 'AbortError'].includes(error.name)
          if (cancelled) return
          if (selected) setPasskeyError('Не удалось войти с ключом. Повторите попытку или войдите по почте.')
          await new Promise<void>(resolve => {
            let done = () => {
              clearTimeout(timer)
              signal.removeEventListener('abort', done)
              resolve()
            }
            let timer = setTimeout(done, retryDelay)
            signal.addEventListener('abort', done, { once: true })
          })
          retryDelay = Math.min(retryDelay * 2, 30_000)
        }
      }
    }
    void signIn()
    return () => {
      controller.abort()
      WebAuthnAbortService.cancelCeremony()
    }
  }, [client, passkeyAutofill, search.token, sendInvitation.isSuccess])

  return (
    <SplashLayout>
      {offerPasskey ?
        <div class={clsx($.form, $layout.col)}>
          <h3>Вход без писем</h3>
          <p>В следующий раз подтверждайте вход на своём устройстве — без ссылки на почте.</p>
          <PasskeyCreate onCreated={finish} />
          <button type='button' class='plain' onClick={finish}>Позже</button>
        </div>
      : sendInvitation.isSuccess ?
        <form.Subscribe selector={(state) => state.values.email}>
          {(email) => (
            <div class={$.success}>
              <p>
                Ссылка для входа отправлена&nbsp;на <b>{email}</b>.
              </p>
              <p class={$type.footnote}>Проверьте почту и&nbsp;перейдите по&nbsp;ссылке.</p>
              <p>
                <a href='/login' class={$type.underline}>
                  Войти с другим адресом
                </a>
              </p>
            </div>
          )}
        </form.Subscribe>
      : <form
          class={$.form}
          style={{ flex: 0 }}
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
        >
          <h3>Вход в личный кабинет</h3>

          <form.Field name='token'>{(field) => <Errors errors={field.state.meta.errors} />}</form.Field>

          {verifyMagicLink.isPending ?
            <div class={$.loading} style={{ fontSize: '2em' }}>
              <Spinner />
            </div>
          : <div class={$layout.col}>
              <form.Field name='email'>
                {(field) => (
                  <>
                    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
                      <div
                        class={clsx($.field, { [$.hasError]: !field.state.meta.isValid && field.state.meta.isTouched })}
                      >
                        <input
                          ref={emailInputRef}
                          name='username'
                          autoComplete='username webauthn'
                          required
                          type='email'
                          pattern="(?!\.)(?!.*\.\.)([a-z0-9_'+\-\.]*)[a-z0-9_+\-]@([a-z0-9][a-z0-9\-]*\.)+[a-z]{2,}"
                          value={field.state.value}
                          onChange={(e) => {
                            field.handleChange(e.target.value)
                            form.resetField('token')
                          }}
                          onBlur={field.handleBlur}
                          disabled={sendInvitation.isPending || devSignIn.isPending}
                          class={$.input}
                          onInput={(event) => {
                            let input = event.target as HTMLInputElement
                            input.setCustomValidity(
                              input.validity.patternMismatch ? 'Неправильный адрес электронной почты.' : ''
                            )
                            let fontSize = 18
                            input.style.fontSize = `${fontSize}px`
                            while (input.scrollWidth > input.clientWidth + 1 && fontSize > 12) {
                              fontSize--
                              input.style.fontSize = `${fontSize}px`
                            }
                          }}
                          placeholder='Электронная почта или passkey'
                        />
                        <form.Subscribe selector={(state) => state.isSubmitting}>
                          {(isSubmitting) => (
                            <button
                              class={`plain ${$.button} ${sendInvitation.isPending || devSignIn.isPending ? $anim.pulse : ''}`}
                              type='submit'
                              disabled={isSubmitting}
                              title={isDev ? 'Войти в development-окружение' : 'Отправить ссылку для входа'}
                            >
                              <ArrowRightCircleFillIcon style={{ height: '32px', width: 'auto' }} />
                            </button>
                          )}
                        </form.Subscribe>
                      </div>
                      {field.state.meta.isTouched && (
                        <Errors
                          errors={field.state.meta.errors}
                          format={{
                            invalid_format: 'Неправильный адрес электронной почты.'
                          }}
                          class={clsx($.error, $type.small)}
                        />
                      )}
                    </div>
                    {!isDev && <p class={clsx($type.footnote, $type.small)}>
                      Мы не используем пароли. Введите свой адрес электронной почты и мы отправим одноразовую ссылку для входа.
                    </p>}
                  </>
                )}
              </form.Field>
              {passkeyError && <p role='alert' class={clsx($type.footnote, $type.small, $.passkeyError)}>{passkeyError}</p>}
            </div>
          }
        </form>
      }
    </SplashLayout>
  )
}
